/**
 * Feed.js — EcoScienceInnova | Researcher Feed
 * Fully integrated with real database data. Mock data removed.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import postService from '../services/postService';
import groupService from '../services/groupService';
import userService from '../services/userService';
import {
  FaNewspaper, FaFlask, FaCalendarAlt, FaSearch, FaUsers, FaGift, FaUserFriends, FaBookOpen
} from 'react-icons/fa';
import Nav from './Nav';
import {
  FeedComposer,
  FeedPostCard,
  FeedCategoryTabs,
  FeedProfileCard,
  FeedOnlineConnections,
  FeedSuggestedGroups,
  FeedUpcomingEvents,
  FeedPeopleYouMayKnow,
  FeedTrendingTopics,
  GrantsModal
} from './feed';
import FeedbackModal from './shared/FeedbackModal';
import './Feed.css';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// ── Category definitions ─────────────────────────────────────────────────────
const CATEGORY_STYLES = {
  articulo: { color: '#3b82f6', label: '📄 Artículo Científico' },
  evento: { color: '#FF6600', label: '📅 Evento' },
  discusion: { color: '#FF6600', label: '👥 Discusión' },
  publicacion: { color: '#10b981', label: '📢 Publicación' },
};

const CATEGORIES = [
  { key: 'all', label: 'Todo', icon: <FaNewspaper /> },
  { key: 'articulo', label: 'Artículos', icon: <FaFlask /> },
  { key: 'evento', label: 'Eventos', icon: <FaCalendarAlt /> },
  { key: 'publicacion', label: 'Publicaciones', icon: <FaNewspaper /> },
  { key: 'discusion', label: 'Discusiones', icon: <FaUsers /> },
];

// ── Main component ────────────────────────────────────────────────────────────
const Feed = () => {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [posts, setPosts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState({ isOpen: false, title: '', message: '', type: 'error' });
  const [isGrantsModalOpen, setIsGrantsModalOpen] = useState(false);

  const showFeedback = (title, message, type = 'error') => {
    setFeedback({ isOpen: true, title, message, type });
  };

  const userRoles = user?.roles || [];
  const isProfessionalOrInvestigator = userRoles.some(r => {
    const roleName = typeof r === 'string' ? r : r.name;
    return ['INVESTIGATOR', 'INSTITUTION', 'HEALTH_PROFESSIONAL'].includes(roleName);
  });

  useEffect(() => {
    loadPosts();
    loadSidebarData();
  }, []);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const data = await postService.getPosts(token);
      setPosts(data);
    } catch (err) {
      console.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const loadSidebarData = async () => {
    try {
      // 1. Load groups
      const groupData = await groupService.getGroups(token);
      setGroups(groupData.slice(0, 5).map(g => ({
        id: g.id,
        name: g.name,
        members: g.members?.length || 0,
        icon: <FaUserFriends style={{ color: '#FF6600' }} />
      })));

      // 2. Load contacts & pending requests for filtering
      const [contactData, pendingRec, pendingSent] = await Promise.all([
        userService.getContacts(token),
        userService.getPendingConnections(token),
        userService.getSentPendingConnections(token)
      ]);

      const mappedContacts = contactData.map(c => {
        const lastActive = c.lastActive ? new Date(c.lastActive) : null;
        const isOnline = lastActive && (new Date() - lastActive) < 5 * 60 * 1000;

        return {
          id: c.id,
          name: `${c.profile?.firstName || ''} ${c.profile?.lastName || ''}`.trim() || 'Investigador',
          role: c.professionalProfile?.profession || 'Investigador',
          online: !!isOnline,
          photo: c.profile?.photoUrl,
        };
      });
      setContacts(mappedContacts);

      // 3. Load suggestions (researchers not in contacts or pending)
      const researcherData = await userService.getResearchers(token);

      // Collect all IDs to filter out
      const excludedIds = new Set([
        user?.id,
        ...contactData.map(c => c.id),
        ...pendingRec.map(r => r.requester?.id),
        ...pendingSent.map(r => r.recipient?.id)
      ]);

      const mappedSuggestions = researcherData
        .filter(r => r.id && !excludedIds.has(r.id))
        .map(r => ({
          id: r.id,
          name: `${r.profile?.firstName || ''} ${r.profile?.lastName || ''}`.trim() || 'Investigador',
          role: r.professionalProfile?.profession || 'Investigador',
          photo: r.profile?.photoUrl,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      setSuggestions(mappedSuggestions);
    } catch (err) {
      console.error('Failed to load sidebar data', err);
    }
  };

  const handleJoinGroup = async (groupId) => {
    try {
      await groupService.requestJoin(groupId, token);
      showFeedback('Solicitud enviada', 'Tu solicitud para unirte al grupo ha sido enviada con éxito.', 'success');
      loadSidebarData();
    } catch (err) {
      const errorMsg = err.response?.data?.msg || err.msg || 'No se pudo enviar la solicitud.';
      showFeedback('Error al unirse', errorMsg, 'error');
    }
  };

  const handleConnectSuggestion = async (userId) => {
    try {
      await userService.sendConnectionRequest(userId, token);
      showFeedback('Solicitud enviada', 'Se ha enviado la invitación de conexión.', 'success');
      loadSidebarData();
    } catch (err) {
      showFeedback('Error', 'No se pudo enviar la solicitud.', 'error');
    }
  };

  const handleEditProfile = () => {
    navigate(isProfessionalOrInvestigator ? '/profile/edit' : '/profile-patient/edit');
  };

  const handlePublish = async (postData) => {
    try {
      const newPost = await postService.createPost(postData, token);
      setPosts(prev => [newPost, ...prev]);
    } catch (err) {
      const errorMsg = err.response?.data?.msg || err.msg || 'No pudimos publicar tu contenido.';
      showFeedback('Error al publicar', errorMsg, 'error');
    }
  };

  const filtered = posts.filter(p => {
    // 1. Basic category filter (Todo, Artículos, etc.)
    const categoryMatch = activeCategory === 'all' || p.type === activeCategory;

    // 2. Network module filter: Researchers should only see REDRESEARCH content
    const authorRoles = p.author?.roles || [];
    const isResearchContent = authorRoles.some(r => {
      const mod = (typeof r === 'string' ? '' : r.module)?.toUpperCase();
      return mod === 'REDRESEARCH';
    });

    return categoryMatch && isResearchContent;
  });

  return (
    <div className="fd-page">
      <Nav />
      <div className="fd-layout">

        {/* ── LEFT SIDEBAR ── */}
        <aside className="fd-left fd-left-scroll">
          <FeedProfileCard
            user={user}
            statsLeft={{ label: 'Publicaciones', value: posts.length }}
            statsRight={{ label: 'Conexiones', value: contacts.length }}
            onEditProfile={handleEditProfile}
          />

          {/* Navigation links */}
          <div className="fd-sidebar-card">
            <p className="fd-sidebar-title">{t('sidebar.platform')}</p>
            {[
              { icon: <FaUsers />, label: t('sidebar.red'), path: '/researcher-network' },
              { icon: <FaSearch />, label: t('sidebar.scientificSearch'), path: '/scientific-search' },
              { icon: <FaBookOpen />, label: 'Biblioteca', path: '/researcher-library' },
              { icon: <FaCalendarAlt />, label: t('sidebar.eventsWebinars'), path: '/scientific-events' },
              { icon: <FaGift />, label: t('sidebar.grants'), action: 'SHOW_GRANTS' },
              { icon: <FaUserFriends />, label: t('sidebar.grupos'), path: '/researcher-groups' },
              { icon: <FaFlask />, label: t('sidebar.projects'), path: '/clinical-projects' },
            ].map(item => (
              <button
                key={item.label}
                className="fd-nav-item"
                onClick={() => item.action === 'SHOW_GRANTS' ? setIsGrantsModalOpen(true) : navigate(item.path)}
              >
                <span className="fd-nav-icon">{item.icon}</span>
                {item.label}
              </button>
            ))}
          </div>

          <FeedSuggestedGroups groups={groups} onJoin={handleJoinGroup} />
        </aside>

        {/* ── CENTER FEED ── */}
        <main className="fd-center">
          <div className="fd-center-sticky">
            <FeedComposer
              user={user}
              onPublish={handlePublish}
              showCategories
              categoryStyles={CATEGORY_STYLES}
              placeholder="Comparte un hallazgo, artículo o reflexión científica..."
              onShowFeedback={showFeedback}
            />

            <FeedCategoryTabs
              categories={CATEGORIES}
              activeKey={activeCategory}
              onChange={setActiveCategory}
            />
          </div>

          <div className="fd-center-scroll">
            {loading ? (
              <div className="fd-loading-state">
                <div className="fd-loading-spinner" />
                <p>Cargando publicaciones...</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="fd-empty-state">
                <span>🔬</span>
                <p>No hay publicaciones en esta categoría aún.</p>
              </div>
            ) : (
              filtered.map(post => (
                <FeedPostCard
                  key={post.id}
                  post={post}
                  user={user}
                  token={token}
                  variant="researcher"
                  categoryStyles={CATEGORY_STYLES}
                  onShowFeedback={showFeedback}
                  onDelete={(id) => setPosts(prev => prev.filter(p => p.id !== id))}
                  onHide={(id) => setPosts(prev => prev.filter(p => p.id !== id))}
                  onUpdate={(updated) => setPosts(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p))}
                />
              ))
            )}
          </div>
        </main>

        {/* ── RIGHT SIDEBAR ── */}
        <aside className="fd-right fd-right-scroll">
          <FeedPeopleYouMayKnow
            people={suggestions}
            onAdd={handleConnectSuggestion}
          />
          <FeedOnlineConnections
            connections={contacts}
            onMessage={(connectionId) => navigate(`/chat?userId=${connectionId}`)}
          />
        </aside>
      </div>

      <FeedbackModal
        isOpen={feedback.isOpen}
        onClose={() => setFeedback({ ...feedback, isOpen: false })}
        title={feedback.title}
        message={feedback.message}
        type={feedback.type}
      />
      <GrantsModal
        isOpen={isGrantsModalOpen}
        onClose={() => setIsGrantsModalOpen(false)}
        token={token}
      />
    </div>
  );
};

export default Feed;